import { useFormik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Form,
  FormFeedback,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";

import {
  CopyCutLessonRequest,
  OrganizingLessonDto,
} from "../../interfaces";
import { ActivityButton } from "../../components/Common";
import { ChapterSelect } from "../../components/Selects/ChapterSelect";
import { useCopyCutLessonMutation } from "../../store/api/lessonApi";

type TProps = {
  subjectId: string;
  lesson: OrganizingLessonDto;
  show: boolean;
  onCloseClick: (event: any) => void;
  action: string;
};
export const CopyCutLessonModal: React.FC<TProps> = ({
  subjectId,
  lesson,
  show,
  onCloseClick,
  action,
}) => {
  const { t, i18n } = useTranslation();

  const [copyCutLesson, { isLoading: copyCuttingLesson, error: copyCuttingRawError }] =
    useCopyCutLessonMutation();

  const copyCuttingError = copyCuttingRawError
    ? ((copyCuttingRawError as any)?.data?.message || "An error occurred")
    : undefined;

  //#region form
  const form = useFormik<CopyCutLessonRequest>({
    enableReinitialize: true,
    initialValues: {
      action: action,
      lessonId: lesson.id,
      copyingToChapterId: "",
    },
    validationSchema: Yup.object({
      lessonId: Yup.string().required(),
      copyingToChapterId: Yup.string().required(),
    }),
    onSubmit: async (values) => {
      try {
        await copyCutLesson(values).unwrap();
        if (action.toLowerCase() === "copy") {
          toast.success(t("lessonCopyingCompleted"), {
            autoClose: 3000,
          });
        } else if (action.toLowerCase() === "cut") {
          toast.success(t("lessonCutCompleted"), {
            autoClose: 3000,
          });
        }
        onClose();
      } catch (err) {
        console.error(err);
      }
    },
  });
  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };

  const getTitle = () => {
    if (action && lesson?.name?.ar && lesson?.name?.en) {
      return `${t(action)} ${
        i18n.language === "ar" ? lesson?.name?.ar : lesson?.name?.ar
      }`;
    } else return `${t(action)}`;
  };

  //#endregion

  return (
    <Modal
      id="copyCutLesson"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      centered
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            onClose();
          }}
        >
          {getTitle()}
        </ModalHeader>
        {typeof copyCuttingError === "string" && (
          <Alert color="danger" className="rounded-0 mb-0">
            <p className="mb-0">{copyCuttingError}</p>
          </Alert>
        )}
        <ModalBody>
          <div className="mb-3">
            <Label htmlFor="copyingToChapterId" className="form-label">
              {t("copyToChapter")}
              <span className="text-danger">*</span>
            </Label>
            <ChapterSelect
              subjectId={subjectId}
              type="select"
              id="copyingToChapterId"
              name="copyingToChapterId"
              className="form-select"
              aria-label={t("selectChapter")}
              placeholder={t("selectChapter")}
              value={form.values.copyingToChapterId}
              onChange={(change) => {
                form.handleChange(change);
              }}
              onBlur={form.handleBlur}
              invalid={
                form.touched.copyingToChapterId &&
                form.errors.copyingToChapterId
                  ? true
                  : false
              }
            ></ChapterSelect>
            {form.touched.copyingToChapterId &&
            form.errors.copyingToChapterId ? (
              <FormFeedback type="invalid">
                {form.errors.copyingToChapterId}
              </FormFeedback>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onCloseClick}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title={t(action)}
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={copyCuttingLesson}
            disabled={copyCuttingLesson}
          ></ActivityButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
